import React, { useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { ImageIcon, Video, X, Pencil } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { PhotoEditorModal } from '@/components/snap/PhotoEditorModal';
import { compressImage } from '@/utils/imageCompression';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LocalPhoto {
  id: string;
  localUrl: string;          // blob URL for preview (or existingUrl for pre-existing)
  file: File | null;         // null means it's a pre-existing photo (already on Supabase)
  existingUrl: string | null; // non-null for photos already uploaded to Supabase
}

interface LocalVideo {
  id: string;
  localUrl: string; // blob URL for preview
  file: File;
}

export interface SubmissionMediaUploaderHandle {
  /**
   * Upload all pending files to Supabase Storage and return the final public URLs.
   * Pre-existing photos/videos (existingUrl set) are returned as-is without re-upload.
   */
  uploadAll(): Promise<{ imageUrls: string[]; videoUrls: string[] }>;
}

interface SubmissionMediaUploaderProps {
  imageUrls: string[];
  videoUrls: string[];
  onImagesChange: (urls: string[]) => void;
  onVideosChange: (urls: string[]) => void;
  isVideoContext?: boolean;
}

const MAX_IMAGES = 5;
const MAX_VIDEOS = 3;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;   // 5 MB
const MAX_VIDEO_SIZE = 100 * 1024 * 1024;  // 100 MB

async function uploadToStorage(file: File, bucket: string): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const ext = file.name.split('.').pop() || 'bin';
  const path = `${user.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
  });
  if (error) throw error;

  const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(path);
  return publicUrl;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SubmissionMediaUploader = forwardRef<
  SubmissionMediaUploaderHandle,
  SubmissionMediaUploaderProps
>(({
  imageUrls,
  videoUrls: _videoUrls, // kept for API compatibility; internal state drives display
  onImagesChange,
  onVideosChange,
  isVideoContext = false,
}, ref) => {
  // Initialise from imageUrls prop so edit-mode pre-existing photos are shown
  const [localPhotos, setLocalPhotos] = useState<LocalPhoto[]>(() =>
    imageUrls.map(url => ({ id: url, localUrl: url, file: null, existingUrl: url }))
  );
  const [localVideos, setLocalVideos] = useState<LocalVideo[]>([]);
  const [editingPhotoId, setEditingPhotoId] = useState<string | null>(null);

  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const editingPhoto = localPhotos.find(p => p.id === editingPhotoId) ?? null;

  // ---------------------------------------------------------------------------
  // Imperative handle — called by parent at submit time
  // ---------------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    async uploadAll() {
      // Upload any new images (file !== null); pass-through existing URLs
      const imageUrls = await Promise.all(
        localPhotos.map(async (p) => {
          if (p.existingUrl && !p.file) return p.existingUrl;
          if (!p.file) return '';
          let fileToUpload = p.file;
          if (fileToUpload.size > 500 * 1024) {
            fileToUpload = await compressImage(fileToUpload);
          }
          return uploadToStorage(fileToUpload, 'post-images');
        })
      );

      // Upload all video files
      const videoUrls = await Promise.all(
        localVideos.map(v => uploadToStorage(v.file, 'post-videos'))
      );

      return {
        imageUrls: imageUrls.filter(Boolean),
        videoUrls,
      };
    },
  }));

  // ---------------------------------------------------------------------------
  // Image handlers — store locally, no upload yet
  // ---------------------------------------------------------------------------
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image must be under 5 MB');
      e.target.value = '';
      return;
    }

    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const localUrl = URL.createObjectURL(file);
    const updated = [...localPhotos, { id, localUrl, file, existingUrl: null }];
    setLocalPhotos(updated);
    onImagesChange(updated.map(p => p.localUrl));

    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleEditorSave = (blob: Blob, previewUrl: string) => {
    const photoId = editingPhotoId;
    if (!photoId) return;
    setEditingPhotoId(null);

    const newFile = new File([blob], 'edited.jpg', { type: 'image/jpeg' });
    const updated = localPhotos.map(p =>
      p.id === photoId
        ? { ...p, localUrl: previewUrl, file: newFile, existingUrl: null }
        : p
    );
    setLocalPhotos(updated);
    onImagesChange(updated.map(p => p.localUrl));
  };

  const removeImage = (id: string) => {
    const updated = localPhotos.filter(p => p.id !== id);
    setLocalPhotos(updated);
    onImagesChange(updated.map(p => p.localUrl));
  };

  // ---------------------------------------------------------------------------
  // Video handlers — store locally, no upload yet
  // ---------------------------------------------------------------------------
  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_VIDEO_SIZE) {
      toast.error('Video must be under 100 MB');
      e.target.value = '';
      return;
    }

    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const localUrl = URL.createObjectURL(file);
    const updated = [...localVideos, { id, localUrl, file }];
    setLocalVideos(updated);
    onVideosChange(updated.map(v => v.localUrl));

    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const removeVideo = (id: string) => {
    const updated = localVideos.filter(v => v.id !== id);
    setLocalVideos(updated);
    onVideosChange(updated.map(v => v.localUrl));
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-4">
      {/* Photos */}
      <div>
        <Label className="text-foreground font-medium">
          {isVideoContext ? 'Upload Cover Photo *' : 'Photos *'}
        </Label>
        <div className="flex gap-2 overflow-x-auto py-2 pb-1">
          {localPhotos.map((photo, i) => (
            <div key={photo.id} className="relative flex-shrink-0 w-24 h-24">
              {i === 0 && (
                <span className="absolute top-1 left-1 z-10 text-[10px] font-semibold bg-black/60 text-white px-1.5 py-0.5 rounded">
                  Cover
                </span>
              )}
              <img
                src={photo.localUrl}
                alt={`Photo ${i + 1}`}
                className="w-full h-full object-cover rounded-xl border border-border"
              />
              <button
                type="button"
                onClick={() => setEditingPhotoId(photo.id)}
                className="absolute bottom-1 left-1 z-10 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-black/80 transition-colors"
                aria-label="Edit photo"
              >
                <Pencil className="w-3 h-3" />
              </button>
              <button
                type="button"
                onClick={() => removeImage(photo.id)}
                className="absolute top-1 right-1 z-10 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-black/80 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}

          {localPhotos.length < MAX_IMAGES && (
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              className="flex-shrink-0 w-24 h-24 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-foreground/40 hover:text-foreground/70 transition-colors"
            >
              <ImageIcon className="w-5 h-5" />
              <span className="text-[10px] font-medium">Add Photo</span>
            </button>
          )}
        </div>
        {localPhotos.length === 0 && (
          <p className="text-xs text-muted-foreground mt-1">
            First photo becomes the dish cover image
          </p>
        )}
      </div>

      {/* Videos */}
      {!isVideoContext && (
        <div>
          <Label className="text-foreground font-medium">Videos (optional)</Label>
          <div className="flex gap-2 overflow-x-auto py-2 pb-1">
            {localVideos.map((video) => (
              <div key={video.id} className="relative flex-shrink-0 w-24 h-24">
                <video
                  src={video.localUrl}
                  className="w-full h-full object-cover rounded-xl border border-border"
                  muted
                  playsInline
                />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="bg-black/50 rounded-full p-1.5">
                    <Video className="w-4 h-4 text-white" />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeVideo(video.id)}
                  className="absolute top-1 right-1 z-10 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-black/80 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}

            {localVideos.length < MAX_VIDEOS && (
              <button
                type="button"
                onClick={() => videoInputRef.current?.click()}
                className="flex-shrink-0 w-24 h-24 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-foreground/40 hover:text-foreground/70 transition-colors"
              >
                <Video className="w-5 h-5" />
                <span className="text-[10px] font-medium">Add Video</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleImageSelect}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        className="hidden"
        onChange={handleVideoSelect}
      />

      {/* Image editor modal */}
      {editingPhoto && (
        <PhotoEditorModal
          isOpen
          imageSrc={editingPhoto.localUrl}
          onSave={handleEditorSave}
          onCancel={() => setEditingPhotoId(null)}
        />
      )}
    </div>
  );
});

SubmissionMediaUploader.displayName = 'SubmissionMediaUploader';
